import React from 'react';
import { useHistory, useLocation } from 'react-router';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { URLModal } from 'src/components/common/URLModal';
import { PublishYourSchemaModal } from 'src/components/publishSchema/PublishYourSchemaModal';
import { PublishToNewGraphModal } from 'src/components/sandboxPublishGraph/PublishToNewGraphModal';
import { SetUpMetrics } from 'src/components/setUpMetrics/SetUpMetrics';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayClosedLocation } from 'src/lib/routing';

import { ConnectionSettingsModal } from '../explorerPage/connectionSettingsModal/ConnectionSettingsModal';
import { EmbedSandboxModal } from '../explorerPage/embedSandboxModal/EmbedSandboxModal';
import { ExplorerBehavioralFlags } from '../explorerPage/explorerBehavioralFlags';
import { ApiKeysModal } from '../graphSettingsPage/apiKeysPage/apiKeysCard/ApiKeysModal';
import { GraphRef } from '../hooks/useGraphRef';
import { EditContractConfigWizard } from '../variantSettingsPage/variantSettingsCards/contractConfigWizard/EditContractConfigWizard';
import { ContractDetailsModal } from '../variantSettingsPage/variantSettingsCards/contractDetailsModal/ContractDetailsModal';
import { EditReadmeModal } from '../variantsHomepage/editReadmeModal/EditReadmeModal';

import { BuildErrorModal } from './buildErrorModal/BuildErrorModal';
import { GraphBehavioralFlags } from './graphBehavioralFlags/graphBehavioralFlags';
import { LaunchErrorModal } from './launchErrorModal/LaunchErrorModal';

export const SharedSandboxAndGraphModals = ({
  currentAccountId,
  explorerBehavioralFlags,
  graphBehavioralFlags,
}: {
  currentAccountId: GraphQLTypes.Maybe<string>;
  explorerBehavioralFlags?: ExplorerBehavioralFlags;
  graphBehavioralFlags: GraphBehavioralFlags;
}) => {
  return (
    <>
      {currentAccountId && (
        <URLModal name={Config.modals.publishSandboxCreateNewGraph}>
          {({ isOpen, closeModal }) => (
            <PublishToNewGraphModal
              isOpen={isOpen}
              onClose={closeModal}
              currentAccountId={currentAccountId}
            />
          )}
        </URLModal>
      )}

      <URLModal name={Config.modals.connectionSettings}>
        {({ isOpen, closeModal }) => (
          <ConnectionSettingsModal
            isOpen={isOpen}
            onClose={() => {
              closeModal();
            }}
            explorerBehavioralFlags={explorerBehavioralFlags}
            graphBehavioralFlags={graphBehavioralFlags}
          />
        )}
      </URLModal>

      <URLModal name={Config.modals.embedSandbox}>
        {({ isOpen, closeModal }) => (
          <EmbedSandboxModal isOpen={isOpen} onClose={closeModal} />
        )}
      </URLModal>
    </>
  );
};

export const VariantLayoutModals = ({
  currentAccountId,
  graphRef,
  graphBehavioralFlags,
  graphTitle,
  variantUrl,
}: {
  currentAccountId: GraphQLTypes.Maybe<string>;
  graphRef: GraphRef;
  graphBehavioralFlags: GraphBehavioralFlags;
  graphTitle: string | undefined;
  variantUrl: string | undefined | null;
}) => {
  const location = useLocation();
  const history = useHistory();

  return (
    <>
      <SharedSandboxAndGraphModals
        currentAccountId={currentAccountId}
        graphBehavioralFlags={graphBehavioralFlags}
      />
      <URLModal name={Config.modals.createGraphApiToken}>
        {({ closeModal }) => (
          <ApiKeysModal onClose={closeModal} graphId={graphRef.graphId} />
        )}
      </URLModal>
      <URLModal name={Config.modals.publishYourSchema}>
        {({ isOpen, closeModal }) => (
          <PublishYourSchemaModal isOpen={isOpen} onClose={closeModal} />
        )}
      </URLModal>
      <URLModal name={Config.modals.setupMetrics}>
        {({ isOpen, closeModal }) => (
          <SetUpMetrics
            graphRef={graphRef}
            isOpen={isOpen}
            onClose={closeModal}
          />
        )}
      </URLModal>
      <LazyUrlModal name={Config.modals.buildErrors}>
        {({ isOpen, closeModal }) => (
          <BuildErrorModal
            isOpen={isOpen}
            onClose={closeModal}
            graphRef={graphRef}
          />
        )}
      </LazyUrlModal>
      <URLModal name={Config.modals.launchErrors}>
        {({ isOpen, closeModal }) => (
          <LaunchErrorModal
            isOpen={isOpen}
            onClose={closeModal}
            graphId={graphRef.graphId}
            graphVariant={graphRef.graphVariant}
          />
        )}
      </URLModal>
      <LazyUrlModal name={Config.modals.editGraphReadme}>
        {({ closeModal, isOpen }) => (
          <EditReadmeModal
            isOpen={isOpen}
            onClose={closeModal}
            graphRef={graphRef}
            graphTitle={graphTitle}
            variantUrl={variantUrl}
          />
        )}
      </LazyUrlModal>
      <URLModal name={Config.modals.viewContractDetails}>
        {({ isOpen }) => (
          <ContractDetailsModal
            isOpen={isOpen}
            onClose={() => {
              // closeModal doesn't support removing search params.
              // Cleaning the URL state here.
              history.replace(
                setOverlayClosedLocation(location, [
                  Config.queryParameters.ContractVariant,
                ]),
              );
            }}
          />
        )}
      </URLModal>
      <URLModal name={Config.modals.editContractConfigWizard}>
        {({ isOpen }) => (
          <EditContractConfigWizard
            isOpen={isOpen}
            // closeModal doesn't support removing search params.
            // Cleaning the URL state here.
            onClose={() => {
              history.replace(
                setOverlayClosedLocation(location, [
                  Config.queryParameters.ContractVariant,
                  Config.queryParameters.SourceVariant,
                ]),
              );
            }}
          />
        )}
      </URLModal>
    </>
  );
};
